import React from 'react';
import { SpaceBetween, FormField, Textarea, Alert, Box, Button, Multiselect, MultiselectProps } from '@amzn/awsui-components-core';

interface CustomPromptInputProps {
  userPrompt: string;
  optimizedPrompt: string;
  suggestions: string[];
  customError: string | null;
  isOptimizing: boolean;
  isGeneratingCustom: boolean;
  isGeneratingCustomChart: boolean;
  selectedActions: string[];
  onPromptChange: (value: string) => void;
  onOptimizePrompt: () => void;
  onSelectionChange: MultiselectProps['onChange'];
  onBlur: () => void;
  onDismissError: () => void;
  placeholder: string;
}

const actionOptions = [
  {
    label: 'Select All',
    options: [
      { value: 'generateSummary', label: 'Generate Custom Summary' },
      { value: 'generateCharts', label: 'Generate Custom Charts' }
    ]
  }
];

export const CustomPromptInput: React.FC<CustomPromptInputProps> = ({
  userPrompt,
  optimizedPrompt,
  suggestions,
  customError,
  isOptimizing,
  isGeneratingCustom,
  isGeneratingCustomChart,
  selectedActions,
  onPromptChange,
  onOptimizePrompt,
  onSelectionChange,
  onBlur,
  onDismissError,
  placeholder
}) => {
  return (
    <SpaceBetween direction="vertical" size="xs">
      <FormField
        label="What would you like to analyze?"
        description="Ask specific questions about your data, request deeper insights, or focus on particular metrics."
      >
        <Textarea
          value={userPrompt}
          onChange={({ detail }) => onPromptChange(detail.value)}
          placeholder={placeholder}
          rows={3}
          disabled={isOptimizing || isGeneratingCustom || isGeneratingCustomChart}
        />
      </FormField>

      <SpaceBetween direction="horizontal" size="s">
        <Multiselect
          selectedOptions={selectedActions.map((value) => actionOptions[0].options.find((opt) => opt.value === value)!)}
          onChange={onSelectionChange}
          onBlur={onBlur}
          options={actionOptions}
          placeholder={isGeneratingCustom ? 'Generating Summary...' : isGeneratingCustomChart ? 'Generating Charts...' : 'Generate Custom Insights'}
          disabled={(!userPrompt.trim() && !optimizedPrompt.trim()) || isGeneratingCustom || isGeneratingCustomChart || isOptimizing}
          statusType={isGeneratingCustom || isGeneratingCustomChart ? 'loading' : 'finished'}
          hideTokens
        />

        <Button
          variant="normal"
          iconName="suggestions"
          onClick={onOptimizePrompt}
          loading={isOptimizing}
          disabled={!userPrompt.trim() || isGeneratingCustom || isGeneratingCustomChart}
        >
          Improve My Question
        </Button>
      </SpaceBetween>

      {customError && (
        <Alert type="error" dismissible onDismiss={onDismissError}>
          {customError}
        </Alert>
      )}

      {optimizedPrompt && (
        <Box padding="s" color="text-body-secondary">
          <SpaceBetween direction="vertical" size="xs">
            <Box variant="strong">Improved Question:</Box>
            <Box>{optimizedPrompt}</Box>
            {suggestions.length > 0 && (
              <Box>
                <Box variant="strong">Tips for better results:</Box>
                <ul>
                  {suggestions.map((suggestion, index) => (
                    <li key={index}>{suggestion}</li>
                  ))}
                </ul>
              </Box>
            )}
          </SpaceBetween>
        </Box>
      )}
    </SpaceBetween>
  );
};
